import type { MisconceptionDefinition } from './types'

/**
 * STU — Studievaardigheden misconcepties.
 *
 * STATUS (mei 2026): **Phase B — kebab-canonical registry**.
 *
 * Alle IDs volgen `MISCONCEPTION_REGISTRY_SSOT_2026.md §3`: één dot tussen
 * module-prefix en kebab-case body, geen underscores, geen extra dots
 * (`stu.<sectie>-<beschrijving>`).
 *
 * Deze registry is leidend. Eventuele STU-seeds die nog naar oude
 * (Engels/underscore/dotted) tag-IDs verwijzen worden door
 * `seedConsolidator.normalizeSeedQuestion` opgepikt als unknown-tag
 * en in een latere sweep gereconcilieerd; de registry-correctheid is
 * de SSOT en gaat voor seed-compatibiliteit.
 */
export const STU_MISCONCEPTIONS: MisconceptionDefinition[] = [
  // ───────────────────────────────────────────────────────────
  // STU-ALFABET — alfabetische volgorde
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.alfabet-b-voor-a',
    label: 'Denkt dat B vóór A komt',
    module: 'Studievaardig',
    description:
      'Leerling beheerst de alfabetvolgorde nog niet en plaatst B vóór A, of kiest een random volgorde.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet-subjectieve-volgorde',
    label: 'Subjectieve volgorde i.p.v. alfabet',
    module: 'Studievaardig',
    description:
      'Leerling rangschikt woorden op persoonlijke voorkeur (favoriet, eerste-gezien) i.p.v. de alfabetische conventie.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet-volgorde-optioneel',
    label: 'Volgorde optioneel geacht',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat alfabetisch ordenen optioneel is en plaatst woorden willekeurig.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet-range-te-breed',
    label: 'Range te breed gekozen',
    module: 'Studievaardig',
    description:
      'Leerling kiest een te brede letter-range (bv. A–M i.p.v. C–E) en mist daardoor precisie.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet-tweede-letter-overgeslagen',
    label: 'Tweede letter niet gebruikt',
    module: 'Studievaardig',
    description:
      'Bij woorden met dezelfde beginletter kijkt leerling niet door naar de tweede of derde letter om de volgorde te bepalen.',
    typicalFamilies: ['STU-ALFABET', 'STU-WOORDENBOEK'],
  },
  {
    id: 'stu.alfabet-verkeerde-beginletter',
    label: 'Woord onder verkeerde beginletter',
    module: 'Studievaardig',
    description:
      'Leerling plaatst een woord onder een letter die niet de beginletter is (bv. door verwarring met klank vs. spelling).',
    typicalFamilies: ['STU-ALFABET', 'STU-WOORDENBOEK'],
  },
  {
    id: 'stu.alfabet-z-speciale-positie',
    label: 'Z als speciale positie',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat Z (of een andere randletter) een uitzonderlijke positie heeft binnen het alfabet.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet-verkeerde-middenletter',
    label: 'Letter bij verkeerde middenletter',
    module: 'Studievaardig',
    description:
      'Leerling weet de globale richting (begin/eind) maar plaatst de letter bij de verkeerde middenletter.',
    typicalFamilies: ['STU-ALFABET'],
  },
  {
    id: 'stu.alfabet-positie-niet-relevant',
    label: 'Positie binnen alfabet doet er niet toe',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat de exacte positie binnen het alfabet niet uitmaakt voor ordening of opzoeken.',
    typicalFamilies: ['STU-ALFABET'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-COMBINEER — meerdere bronnen combineren
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.zoek-op-opdracht',
    label: 'Zoekt op de opdracht i.p.v. het onderwerp',
    module: 'Studievaardig',
    description:
      'Leerling typt de letterlijke vraag in een zoekmachine i.p.v. een kernwoord uit het onderwerp.',
    typicalFamilies: ['STU-COMBINEER', 'STU-INTERNET'],
  },
  {
    id: 'stu.zoekterm-te-breed',
    label: 'Zoekterm te breed',
    module: 'Studievaardig',
    description:
      'Leerling gebruikt één algemeen woord (bv. "dieren") i.p.v. een specifieke samengestelde zoekterm.',
    typicalFamilies: ['STU-COMBINEER', 'STU-INTERNET'],
  },
  {
    id: 'stu.weer-vs-klimaat',
    label: 'Weer en klimaat verward',
    module: 'Studievaardig',
    description:
      'Leerling gebruikt "weer" (dag-fluctuatie) en "klimaat" (gemiddelde over jaren) door elkaar bij het selecteren van bronnen.',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.conclusie-buiten-bron',
    label: 'Conclusie buiten bron',
    module: 'Studievaardig',
    description:
      'Leerling trekt een conclusie die niet door de gegeven bronnen wordt ondersteund (verzint of voegt eigen kennis toe).',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.bron-negeren',
    label: 'Bron genegeerd',
    module: 'Studievaardig',
    description:
      'Leerling negeert relevante cijfers of citaten in de aangeboden bronnen.',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.bronnen-vergelijken-ipv-combineren',
    label: 'Bronnen vergeleken i.p.v. gecombineerd',
    module: 'Studievaardig',
    description:
      'Leerling zet bronnen naast elkaar (vergelijken) i.p.v. ze samen te gebruiken om één conclusie te bouwen.',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.een-bron-volstaat',
    label: 'Eén bron volstaat',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat één bron genoeg is voor een onderbouwde conclusie, terwijl de opdracht expliciet om combineren vraagt.',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.bron-doelgroep-verwart',
    label: 'Doelgroep van bron verward',
    module: 'Studievaardig',
    description:
      'Leerling past informatie uit een bron toe op de verkeerde doelgroep (bv. volwassen-richtlijnen op kinderen).',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.anekdote-als-bewijs',
    label: 'Anekdote als bewijs',
    module: 'Studievaardig',
    description:
      'Leerling beschouwt persoonlijke verhalen of vrienden-uitspraken als gelijkwaardig bewijs aan systematische bronnen.',
    typicalFamilies: ['STU-COMBINEER'],
  },
  {
    id: 'stu.commercieel-als-onafhankelijk',
    label: 'Commerciële bron als onafhankelijk',
    module: 'Studievaardig',
    description:
      'Leerling onderscheidt commerciële (reclame, fabrikant) en onafhankelijke bronnen niet.',
    typicalFamilies: ['STU-COMBINEER', 'STU-INTERNET'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-DIAGRAM — diagram-keuze en -lezen
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.diagram-kwart-derde-verward',
    label: '¼ en ⅓ verward',
    module: 'Studievaardig',
    description:
      'Leerling herkent het verschil tussen een kwart-segment en een derde-segment in een cirkeldiagram niet visueel.',
    typicalFamilies: ['STU-DIAGRAM', 'STU-CIRKEL'],
  },
  {
    id: 'stu.diagram-percentage-als-aantal',
    label: 'Percentage als aantal gelezen',
    module: 'Studievaardig',
    description:
      'Leerling leest een percentage in het diagram als absoluut aantal i.p.v. relatief deel.',
    typicalFamilies: ['STU-DIAGRAM', 'STU-CIRKEL'],
  },
  {
    id: 'stu.diagram-kwart-half-verward',
    label: '¼ en ½ verward',
    module: 'Studievaardig',
    description: 'Leerling verwart een kwart met een half in cirkeldiagrammen.',
    typicalFamilies: ['STU-DIAGRAM', 'STU-CIRKEL'],
  },
  {
    id: 'stu.diagram-cirkel-voor-tijdverloop',
    label: 'Cirkeldiagram voor tijdverloop',
    module: 'Studievaardig',
    description:
      'Leerling kiest een cirkeldiagram (deel-van-geheel) waar een lijndiagram (verloop) passender is.',
    typicalFamilies: ['STU-DIAGRAM'],
  },
  {
    id: 'stu.diagram-staaf-voor-trend',
    label: 'Staafdiagram voor trend',
    module: 'Studievaardig',
    description:
      'Leerling kiest een staafdiagram (categorisch) waar een lijndiagram (trend) passender is.',
    typicalFamilies: ['STU-DIAGRAM'],
  },
  {
    id: 'stu.diagram-pictogram-voor-continu',
    label: 'Pictogram voor continue data',
    module: 'Studievaardig',
    description:
      'Leerling kiest een pictogram (discrete categorieën) voor continue meetgegevens.',
    typicalFamilies: ['STU-DIAGRAM'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-FLOWCHART — procesflowchart lezen
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.flowchart-ruit-rechthoek-verward',
    label: 'Ruit en rechthoek verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een ruit (beslissing) met een rechthoek (actie).',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart-ruit-start-verward',
    label: 'Ruit en begin verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een ruit (beslissing) met een ovaal (start).',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart-ruit-einde-verward',
    label: 'Ruit en einde verward',
    module: 'Studievaardig',
    description: 'Leerling verwart een ruit (beslissing) met een ovaal (eind).',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart-beide-paden-gevolgd',
    label: 'Beide paden tegelijk gevolgd',
    module: 'Studievaardig',
    description:
      'Leerling volgt beide ja/nee-paden uit een beslissingsruit i.p.v. te kiezen.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart-loop-verondersteld',
    label: 'Niet-bestaande loop verondersteld',
    module: 'Studievaardig',
    description:
      'Leerling veronderstelt dat het proces terugkeert terwijl er geen terugpijl in de flowchart staat.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart-beslissing-als-einde',
    label: 'Beslissingsruit gezien als einde',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat het proces stopt bij een beslissingsruit i.p.v. dat er verder wordt gegaan langs ja/nee-pijl.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart-staafdiagram-verward',
    label: 'Flowchart en staafdiagram verward',
    module: 'Studievaardig',
    description:
      'Leerling kent de procesfunctie van een flowchart niet en verwart het met een staafdiagram.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart-cirkeldiagram-verward',
    label: 'Flowchart en cirkeldiagram verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een flowchart met een cirkeldiagram (deel-van-geheel).',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart-lijndiagram-verward',
    label: 'Flowchart en lijndiagram verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een flowchart met een lijndiagram (verloop over tijd).',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart-terugpijl-als-fout',
    label: 'Terugpijl gezien als fout',
    module: 'Studievaardig',
    description:
      'Leerling interpreteert een terugpijl (loop) als tekening-fout i.p.v. bewuste herhaling.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart-loop-herstart-alles',
    label: 'Loop herstart hele proces',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat een terugpijl het hele proces opnieuw start i.p.v. alleen de loop-stap herhaalt.',
    typicalFamilies: ['STU-FLOWCHART'],
  },
  {
    id: 'stu.flowchart-loop-als-einde',
    label: 'Loop als einde gezien',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat een terugpijl markeert dat het proces eindigt.',
    typicalFamilies: ['STU-FLOWCHART'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-INHOUDSOPGAVE — inhoudsopgave gebruiken
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.inhoud-woordenlijst-verward',
    label: 'Inhoudsopgave en woordenlijst verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een inhoudsopgave (hoofdstukken) met een woordenlijst (begrippen).',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-auteursinfo-verwacht',
    label: 'Auteursinfo in inhoudsopgave verwacht',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat schrijver- of uitgever-informatie in de inhoudsopgave staat.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-flaptekst-verward',
    label: 'Inhoudsopgave en flaptekst verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart een inhoudsopgave (structuur) met een flaptekst (samenvatting).',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-verkeerd-hoofdstuk',
    label: 'Verkeerd hoofdstuk gekozen',
    module: 'Studievaardig',
    description:
      'Leerling kiest het paginanummer van een ander hoofdstuk dan gevraagd.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-aangrenzend-hoofdstuk',
    label: 'Verkeerd hoofdstuk gekozen (variant)',
    module: 'Studievaardig',
    description:
      'Tweede variant van "verkeerd hoofdstuk": leerling kiest aangrenzend hoofdstuk i.p.v. het gevraagde.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-paginanummer-verzonnen',
    label: 'Paginanummer verzonnen',
    module: 'Studievaardig',
    description:
      'Leerling verzint een paginanummer i.p.v. de inhoudsopgave te raadplegen.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-startpagina-als-lengte',
    label: 'Startpagina als hoofdstuklengte',
    module: 'Studievaardig',
    description:
      'Leerling geeft de startpagina van een hoofdstuk als antwoord op "hoe lang is dit hoofdstuk?".',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-volgende-start-als-lengte',
    label: 'Volgende startpagina als hoofdstuklengte',
    module: 'Studievaardig',
    description:
      'Leerling geeft de startpagina van het volgende hoofdstuk i.p.v. te subtraheren om de lengte te krijgen.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-off-by-one',
    label: 'Off-by-one bij pagina-telling',
    module: 'Studievaardig',
    description:
      'Leerling maakt een 1-pagina afwijking bij het berekenen van hoofdstuklengte uit startpaginas.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-register-verward',
    label: 'Inhoudsopgave en register verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart de inhoudsopgave (voorin, hoofdstuk-volgorde) met het register (achterin, alfabetisch).',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-midden-verwacht',
    label: 'Inhoudsopgave in midden verwacht',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat de inhoudsopgave in het midden van het boek staat i.p.v. voorin.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-flaptekst-geconsulteerd',
    label: 'Inhoudsopgave en flaptekst verward (consultatie)',
    module: 'Studievaardig',
    description:
      'Leerling kijkt op de flaptekst om inhoudsopgave-informatie te vinden.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-hele-boek-doorlezen',
    label: 'Hele boek doorlezen',
    module: 'Studievaardig',
    description:
      'Leerling leest het hele boek door om een hoofdstuk te vinden i.p.v. de inhoudsopgave te gebruiken.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-willekeurig-bladeren',
    label: 'Willekeurig bladeren',
    module: 'Studievaardig',
    description:
      'Leerling bladert willekeurig door het boek i.p.v. de inhoudsopgave te gebruiken als index.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },
  {
    id: 'stu.inhoud-flaptekst-als-eerste',
    label: 'Flaptekst als eerste oriëntatie',
    module: 'Studievaardig',
    description:
      'Leerling begint bij de flaptekst voor structuur-info terwijl de inhoudsopgave die rol vervult.',
    typicalFamilies: ['STU-INHOUDSOPGAVE'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-MINDMAP — mindmap-structuur
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.mindmap-details-in-midden',
    label: 'Details in het midden',
    module: 'Studievaardig',
    description:
      'Leerling plaatst details in het centrum i.p.v. het hoofdonderwerp.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap-conclusie-in-midden',
    label: 'Conclusie in het midden',
    module: 'Studievaardig',
    description:
      'Leerling verwart mindmap-centrum met de conclusie van een samenvatting.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap-bronnen-in-midden',
    label: 'Bronnenlijst in het midden',
    module: 'Studievaardig',
    description:
      'Leerling verwart het centrum van een mindmap met een bronnen- of literatuurlijst.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap-kleur-als-decoratie',
    label: 'Kleur als decoratie gezien',
    module: 'Studievaardig',
    description:
      'Leerling ziet kleuren in een mindmap als versiering i.p.v. als informatie-grouperende code.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap-kleur-verplicht',
    label: 'Kleur verplicht geacht',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat een mindmap niet correct is zonder kleur, terwijl kleur optioneel-functioneel is.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap-kleur-meer-tekst',
    label: 'Kleur verward met tekstcapaciteit',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat een gekleurde tak meer tekst kan dragen dan een ongekleurde.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap-hele-zinnen-op-takken',
    label: 'Hele zinnen op takken',
    module: 'Studievaardig',
    description:
      'Leerling schrijft hele zinnen op mindmap-takken i.p.v. kernwoorden.',
    typicalFamilies: ['STU-MINDMAP', 'STU-NOTITIES'],
  },
  {
    id: 'stu.mindmap-paginanummers',
    label: 'Mindmap met paginanummers',
    module: 'Studievaardig',
    description:
      'Leerling verwart mindmap (begrip-relaties) met inhoudsopgave (paginanummers).',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap-vragen-als-takken',
    label: 'Vragen als takken',
    module: 'Studievaardig',
    description:
      'Leerling verwart mindmap-structuur met een vragenstrategie (KWL — Know/Want/Learned).',
    typicalFamilies: ['STU-MINDMAP'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-NOTITIES — notities maken
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.notities-letterlijk-overschrijven',
    label: 'Letterlijk overschrijven',
    module: 'Studievaardig',
    description:
      'Leerling probeert alles letterlijk op te schrijven i.p.v. te parafraseren tot kernwoorden.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities-esthetiek-boven-functie',
    label: 'Functie en esthetiek verward',
    module: 'Studievaardig',
    description:
      'Leerling besteedt energie aan mooie notities i.p.v. aan informatie-dichtheid en herbruikbaarheid.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities-als-sociaal-signaal',
    label: 'Notities als sociaal signaal',
    module: 'Studievaardig',
    description:
      'Leerling maakt notities om aandachtig over te komen i.p.v. om later te kunnen leren.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities-hele-zinnen',
    label: 'Hele zinnen in notities',
    module: 'Studievaardig',
    description:
      'Leerling schrijft hele zinnen in notities i.p.v. compacte kernwoorden.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities-alleen-titels',
    label: 'Alleen titels genoteerd',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat de hoofdstuk- of paragraaftitel volstaat als notitie.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities-een-lange-zin',
    label: 'Eén lange zin',
    module: 'Studievaardig',
    description:
      'Leerling schrijft notities als één lange, ongestructureerde zin.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities-pas-voor-toets',
    label: 'Pas voor toets bekijken',
    module: 'Studievaardig',
    description:
      'Leerling wacht tot vlak voor de toets om notities terug te lezen, mist daardoor spaced-repetition effect.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities-nooit-teruglezen',
    label: 'Nooit teruglezen',
    module: 'Studievaardig',
    description:
      'Leerling bekijkt notities na het maken nooit meer; notities als wegwerp-handeling.',
    typicalFamilies: ['STU-NOTITIES'],
  },
  {
    id: 'stu.notities-alleen-op-verzoek',
    label: 'Alleen op verzoek leraar',
    module: 'Studievaardig',
    description:
      'Leerling raadpleegt notities alleen als de leraar daarom vraagt, geen eigen leer-strategie.',
    typicalFamilies: ['STU-NOTITIES'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-SAMENVATTEN — samenvattingen
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.samenvatten-schrijven-voor-lezen',
    label: 'Schrijven zonder lezen',
    module: 'Studievaardig',
    description:
      'Leerling begint te schrijven zonder eerst de tekst overzichtelijk te lezen of te scannen.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten-alleen-eerste-alinea',
    label: 'Alleen eerste alinea dekt alles',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat de eerste alinea altijd de hele tekst samenvat.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten-woordstudie-ipv-leestechniek',
    label: 'Woordstudie i.p.v. leestechniek',
    module: 'Studievaardig',
    description:
      'Leerling verwart samenvatten met begrippen-opzoeken; behandelt het als woordstudie.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten-letterlijk-overschrijven',
    label: 'Samenvatten = overschrijven',
    module: 'Studievaardig',
    description:
      'Leerling verwart samenvatten met letterlijk overschrijven van zinnen.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten-titel-als-samenvatting',
    label: 'Titel als samenvatting',
    module: 'Studievaardig',
    description: 'Leerling denkt dat de titel een volledige samenvatting is.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten-alles-parafraseren',
    label: 'Alles parafraseren (te lang)',
    module: 'Studievaardig',
    description:
      'Leerling parafraseert elke zin afzonderlijk en eindigt met een samenvatting die langer is dan het origineel.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten-hoofdgedachte-weggelaten',
    label: 'Hoofdgedachte weggelaten',
    module: 'Studievaardig',
    description:
      'Leerling laat de hoofdgedachte weg in een samenvatting, focust op secundaire details.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten-belangrijke-punten-weggelaten',
    label: 'Belangrijke punten weggelaten',
    module: 'Studievaardig',
    description:
      'Leerling laat belangrijke ondersteunende punten weg en houdt alleen anekdotes over.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten-conclusie-weggelaten',
    label: 'Conclusie weggelaten',
    module: 'Studievaardig',
    description:
      'Leerling laat de conclusie/synthese weg uit een samenvatting.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
  {
    id: 'stu.samenvatten-voorbeeld-als-hoofdzaak',
    label: 'Voorbeeld als hoofdzaak',
    module: 'Studievaardig',
    description:
      'Leerling neemt een specifiek voorbeeld op in de samenvatting in plaats van de hoofdzaak.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-CIRKEL — cirkeldiagram-specifiek
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.cirkel-som-niet-100',
    label: 'Som is niet 100%',
    module: 'Studievaardig',
    description:
      'Leerling herkent niet dat een cirkeldiagram altijd optelt tot 100%.',
    typicalFamilies: ['STU-CIRKEL'],
  },
  {
    id: 'stu.cirkel-kleinste-grootste-omgedraaid',
    label: 'Kleinste en grootste omgedraaid',
    module: 'Studievaardig',
    description: 'Leerling verwart het kleinste segment met het grootste.',
    typicalFamilies: ['STU-CIRKEL'],
  },
  {
    id: 'stu.cirkel-percentage-breuk-koppeling',
    label: 'Percentage en breuk verkeerd gekoppeld',
    module: 'Studievaardig',
    description:
      'Leerling koppelt een percentage aan de verkeerde breuk (bijv. 25% aan 1/2).',
    typicalFamilies: ['STU-CIRKEL'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-LIJN — lijndiagrammen (basis, trend & verschil)
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.lijn-steilheid-genegeerd',
    label: 'Steilheid genegeerd',
    module: 'Studievaardig',
    description:
      'Leerling negeert de steilheid van de lijn bij het bepalen van de snelste stijging/daling.',
    typicalFamilies: ['STU-LIJN'],
  },
  {
    id: 'stu.lijn-eindwaarde-zonder-verandering',
    label: 'Eindwaarde zonder verandering',
    module: 'Studievaardig',
    description:
      'Leerling kijkt alleen naar de eindwaarde in plaats van naar de verandering.',
    typicalFamilies: ['STU-LIJN'],
  },
  {
    id: 'stu.lijn-trend-zonder-aflezen',
    label: 'Trend zonder aflezen',
    module: 'Studievaardig',
    description:
      'Leerling trekt een conclusie over de trend zonder de waarden goed af te lezen.',
    typicalFamilies: ['STU-LIJN'],
  },
  {
    id: 'stu.lijn-trend-alleen-stijgend',
    label: 'Trend als alleen-stijgend gelezen',
    module: 'Studievaardig',
    description:
      'Leerling beoordeelt een wisselende trend als stijgend zonder de dalingen in de lijn mee te wegen.',
    typicalFamilies: ['STU-LIJN'],
  },
  {
    id: 'stu.lijn-trend-alleen-dalend',
    label: 'Trend als alleen-dalend gelezen',
    module: 'Studievaardig',
    description:
      'Leerling beoordeelt een wisselende trend als dalend zonder de stijgingen in de lijn mee te wegen.',
    typicalFamilies: ['STU-LIJN'],
  },
  {
    id: 'stu.lijn-trend-gelijk-verondersteld',
    label: 'Trend als gelijk verondersteld',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat de waarden gelijk blijven terwijl ze duidelijk verschillen.',
    typicalFamilies: ['STU-LIJN'],
  },
  {
    id: 'stu.lijn-verschil-beginwaarde-als-groei',
    label: 'Beginwaarde gekozen i.p.v. verschil',
    module: 'Studievaardig',
    description:
      'Leerling geeft de beginwaarde als antwoord op een verschil-/groeivraag in plaats van eindwaarde − beginwaarde.',
    typicalFamilies: ['STU-LIJN'],
  },
  {
    id: 'stu.lijn-verschil-eindwaarde-als-groei',
    label: 'Eindwaarde gekozen i.p.v. verschil',
    module: 'Studievaardig',
    description:
      'Leerling geeft de eindwaarde als antwoord op een verschil-/groeivraag in plaats van eindwaarde − beginwaarde.',
    typicalFamilies: ['STU-LIJN'],
  },
  {
    id: 'stu.lijn-verschil-verkeerde-range',
    label: 'Verschil over verkeerde periode berekend',
    module: 'Studievaardig',
    description:
      'Leerling berekent het verschil over een andere periode dan in de vraag genoemd (bv. vanaf week 1 i.p.v. week 2).',
    typicalFamilies: ['STU-LIJN'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-STAAF — staafdiagram lezen
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.staaf-vergeet-laatste-staaf',
    label: 'Vergeet laatste staaf',
    module: 'Studievaardig',
    description:
      'Leerling vergeet de laatste staaf mee te nemen in de berekening.',
    typicalFamilies: ['STU-STAAF'],
  },
  {
    id: 'stu.staaf-rekenfout-optellen',
    label: 'Rekenfout bij optellen staven',
    module: 'Studievaardig',
    description:
      'Leerling maakt een rekenfout bij het optellen van de waarden van meerdere staven.',
    typicalFamilies: ['STU-STAAF'],
  },
  {
    id: 'stu.staaf-verwachting-boven-data',
    label: 'Verwachting boven data',
    module: 'Studievaardig',
    description:
      'Leerling baseert verwachting op eigen aannames in plaats van op de data in het diagram.',
    typicalFamilies: ['STU-STAAF'],
  },
  {
    id: 'stu.staaf-hoogste-laagste-omgedraaid',
    label: 'Hoogste en laagste omgedraaid',
    module: 'Studievaardig',
    description: 'Leerling verwart de hoogste staaf met de laagste.',
    typicalFamilies: ['STU-STAAF'],
  },
  {
    id: 'stu.staaf-conclusie-zonder-aflezen',
    label: 'Conclusie zonder aflezen',
    module: 'Studievaardig',
    description:
      'Leerling trekt een conclusie zonder de exacte waarden van de staven af te lezen.',
    typicalFamilies: ['STU-STAAF'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-WOORDENBOEK — woordenboek gebruiken
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.woordenboek-afkorting-verward',
    label: 'Afkorting verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart de betekenis van een afkorting in het woordenboek.',
    typicalFamilies: ['STU-WOORDENBOEK'],
  },
  {
    id: 'stu.woordenboek-positie-verkeerd-geschat',
    label: 'Positie verkeerd geschat',
    module: 'Studievaardig',
    description:
      'Leerling schat de positie van een woord in het woordenboek verkeerd in.',
    typicalFamilies: ['STU-WOORDENBOEK'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-KAART — schaal en legenda
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.kaart-alleen-1cm-vermenigvuldigd',
    label: 'Alleen 1 cm vermenigvuldigd',
    module: 'Studievaardig',
    description:
      'Leerling vermenigvuldigt de schaal met 1 in plaats van met de gemeten afstand.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-cm-naar-meter-fout',
    label: 'Fout bij cm naar meter',
    module: 'Studievaardig',
    description:
      'Leerling maakt een fout bij het omrekenen van centimeters naar meters.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-schaal-delen-ipv-vermenigvuldigen',
    label: 'Deelt door schaal i.p.v. vermenigvuldigen',
    module: 'Studievaardig',
    description:
      'Leerling deelt de afstand op de kaart door de schaal in plaats van te vermenigvuldigen.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-eenheid-verward',
    label: 'Eenheid verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart de eenheden (cm, m, km) bij het werken met schaal.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-eenheid-km-vergeten',
    label: 'Vergeet om te rekenen naar km',
    module: 'Studievaardig',
    description: 'Leerling geeft het antwoord in cm of m in plaats van km.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-nullen-tellen-fout',
    label: 'Fout bij nullen tellen',
    module: 'Studievaardig',
    description: 'Leerling telt het verkeerde aantal nullen bij het omrekenen.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-cm-gelijk-aan-km',
    label: 'Stelt cm gelijk aan km',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat de waarde in cm direct de waarde in km is.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-legenda-kleur-assoc-bos',
    label: 'Kleur-associatie bos i.p.v. legenda',
    module: 'Studievaardig',
    description:
      'Leerling associeert een kleur (bruin/groen) met bos in plaats van de legenda te raadplegen.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-legenda-kleur-assoc-water',
    label: 'Kleur-associatie water i.p.v. legenda',
    module: 'Studievaardig',
    description:
      'Leerling verwart kleurassociaties (bv. bruin met water) i.p.v. de legenda te gebruiken.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-legenda-aanname-zonder-kijken',
    label: 'Aanname zonder legenda te raadplegen',
    module: 'Studievaardig',
    description:
      'Leerling verzint de betekenis van een symbool of kleur zonder de legenda te lezen.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-legenda-onvolledig-wandel',
    label: 'Legenda onvolledig gelezen (wandel)',
    module: 'Studievaardig',
    description:
      'Leerling leest de legenda onvolledig en sluit alleen het wandelpad uit.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-legenda-onvolledig-fiets',
    label: 'Legenda onvolledig gelezen (fiets)',
    module: 'Studievaardig',
    description:
      'Leerling leest de legenda onvolledig en sluit alleen het fietspad uit.',
    typicalFamilies: ['STU-KAART'],
  },
  {
    id: 'stu.kaart-legenda-symbool-onderscheid-gemist',
    label: 'Symbool-onderscheid in legenda gemist',
    module: 'Studievaardig',
    description:
      'Leerling mist het onderscheid tussen verschillende padtypen of symbolen in de legenda.',
    typicalFamilies: ['STU-KAART'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-PLANNEN — taken plannen
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.plannen-actie-voor-denken',
    label: 'Actie voor denken',
    module: 'Studievaardig',
    description:
      'Leerling begint direct met de taak zonder eerst een plan te maken.',
    typicalFamilies: ['STU-PLANNEN'],
  },
  {
    id: 'stu.plannen-materiaal-voor-structuur',
    label: 'Materiaal voor structuur',
    module: 'Studievaardig',
    description:
      'Leerling verzamelt materiaal zonder eerst de structuur van de taak te bepalen.',
    typicalFamilies: ['STU-PLANNEN'],
  },
  {
    id: 'stu.plannen-review-als-eerste-stap',
    label: 'Review als eerste stap',
    module: 'Studievaardig',
    description:
      'Leerling plaatst controleren/nakijken als eerste stap in het plan.',
    typicalFamilies: ['STU-PLANNEN'],
  },
  {
    id: 'stu.plannen-uitstelgedrag',
    label: 'Uitstelgedrag',
    module: 'Studievaardig',
    description:
      'Leerling plant taken onrealistisch ver in de toekomst of stelt ze uit.',
    typicalFamilies: ['STU-PLANNEN'],
  },
  {
    id: 'stu.plannen-tijdsdruk-onderschat',
    label: 'Tijdsdruk onderschat',
    module: 'Studievaardig',
    description: 'Leerling plant te weinig tijd in voor een taak.',
    typicalFamilies: ['STU-PLANNEN'],
  },
  {
    id: 'stu.plannen-nut-ontkend',
    label: 'Nut van plannen ontkend',
    module: 'Studievaardig',
    description:
      'Leerling ziet het nut van plannen niet in en kiest voor ad-hoc werken.',
    typicalFamilies: ['STU-PLANNEN'],
  },
  {
    id: 'stu.plannen-deadline-als-optioneel',
    label: 'Deadline als optioneel gezien',
    module: 'Studievaardig',
    description:
      'Leerling behandelt de deadline als een richtlijn in plaats van een harde grens.',
    typicalFamilies: ['STU-PLANNEN'],
  },
  {
    id: 'stu.plannen-taakverdeling-weggelaten',
    label: 'Taakverdeling weggelaten',
    module: 'Studievaardig',
    description: 'Bij groepswerk vergeet de leerling de taken te verdelen.',
    typicalFamilies: ['STU-PLANNEN'],
  },
  {
    id: 'stu.plannen-stappen-weggelaten',
    label: 'Essentiële stappen weggelaten',
    module: 'Studievaardig',
    description:
      'Leerling slaat belangrijke tussenstappen over in de planning.',
    typicalFamilies: ['STU-PLANNEN'],
  },
  {
    id: 'stu.plannen-opgeven-bij-tegenslag',
    label: 'Opgeven bij tegenslag',
    module: 'Studievaardig',
    description:
      'Leerling past het plan niet aan bij tegenslag, maar stopt met de taak.',
    typicalFamilies: ['STU-PLANNEN'],
  },
  {
    id: 'stu.plannen-onaf-werk-accepteren',
    label: 'Onaf werk accepteren',
    module: 'Studievaardig',
    description:
      'Leerling levert onaf werk in in plaats van de planning aan te passen.',
    typicalFamilies: ['STU-PLANNEN'],
  },
  {
    id: 'stu.plannen-probleem-negeren',
    label: 'Probleem negeren',
    module: 'Studievaardig',
    description:
      'Leerling negeert planningsproblemen in de hoop dat ze vanzelf oplossen.',
    typicalFamilies: ['STU-PLANNEN'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-ROUTE — routes lezen en plannen
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.route-afslagen-gemist',
    label: 'Afslagen gemist',
    module: 'Studievaardig',
    description:
      'Leerling mist een of meerdere afslagen in een routebeschrijving.',
    typicalFamilies: ['STU-ROUTE'],
  },
  {
    id: 'stu.route-locatie-als-afslag',
    label: 'Locatie als afslag gezien',
    module: 'Studievaardig',
    description:
      'Leerling ziet een locatie-aanduiding (zoals "aan je linkerhand") als een afslag.',
    typicalFamilies: ['STU-ROUTE'],
  },
  {
    id: 'stu.route-rechtdoor-verondersteld',
    label: 'Rechtdoor verondersteld',
    module: 'Studievaardig',
    description:
      'Leerling veronderstelt ten onrechte dat de route rechtdoor gaat.',
    typicalFamilies: ['STU-ROUTE'],
  },
  {
    id: 'stu.route-symbool-snelweg-verward',
    label: 'Snelweg symbool verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart het symbool voor een fietspad met dat van een snelweg.',
    typicalFamilies: ['STU-ROUTE'],
  },
  {
    id: 'stu.route-symbool-water-verward',
    label: 'Water symbool verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart het symbool voor een fietspad met dat van water.',
    typicalFamilies: ['STU-ROUTE'],
  },
  {
    id: 'stu.route-symbool-natuur-verward',
    label: 'Natuur symbool verward',
    module: 'Studievaardig',
    description:
      'Leerling verwart het symbool voor een fietspad met dat van natuur.',
    typicalFamilies: ['STU-ROUTE'],
  },
  {
    id: 'stu.route-veiligheid-genegeerd',
    label: 'Veiligheid genegeerd',
    module: 'Studievaardig',
    description: 'Leerling kiest de kortste route en negeert de veiligheid.',
    typicalFamilies: ['STU-ROUTE'],
  },
  {
    id: 'stu.route-veiligheid-gelijkgesteld',
    label: 'Veiligheid gelijkgesteld',
    module: 'Studievaardig',
    description: 'Leerling denkt dat alle routes even veilig zijn.',
    typicalFamilies: ['STU-ROUTE'],
  },
  {
    id: 'stu.route-veiligheid-ontkend',
    label: 'Veiligheid ontkend',
    module: 'Studievaardig',
    description: 'Leerling denkt dat geen enkele route veilig is.',
    typicalFamilies: ['STU-ROUTE'],
  },
  {
    id: 'stu.route-eerste-weg-gekozen',
    label: 'Eerste weg gekozen zonder vergelijking',
    module: 'Studievaardig',
    description:
      'Leerling neemt meteen de eerste route die hij ziet, zonder routes te vergelijken.',
    typicalFamilies: ['STU-ROUTE'],
  },
  {
    id: 'stu.route-vriend-als-planner',
    label: 'Vriend gevraagd i.p.v. zelf plannen',
    module: 'Studievaardig',
    description:
      'Leerling laat een vriend de route plannen in plaats van zelf de kaart te lezen.',
    typicalFamilies: ['STU-ROUTE'],
  },
  {
    id: 'stu.route-langste-weg-gekozen',
    label: 'Langste route gekozen i.p.v. kortste',
    module: 'Studievaardig',
    description:
      'Leerling kiest de langste route terwijl de kortste gevraagd was — vraag niet goed gelezen.',
    typicalFamilies: ['STU-ROUTE'],
  },

  // ───────────────────────────────────────────────────────────
  // STU-BIBLIOTHEEK — bibliotheek-conventies
  // ───────────────────────────────────────────────────────────
  {
    id: 'stu.bibliotheek-fictie-nonfictie-verward',
    label: 'Fictie en non-fictie verward',
    module: 'Studievaardig',
    description: 'Leerling verwart fictie met non-fictie.',
    typicalFamilies: ['STU-BIBLIOTHEEK'],
  },
  {
    id: 'stu.bibliotheek-stripboek-als-informatief',
    label: 'Stripboek als informatief',
    module: 'Studievaardig',
    description:
      'Leerling denkt dat een stripboek altijd een informatief boek is.',
    typicalFamilies: ['STU-BIBLIOTHEEK'],
  },
  {
    id: 'stu.bibliotheek-poezie-als-informatief',
    label: 'Poëzie als informatief',
    module: 'Studievaardig',
    description: 'Leerling denkt dat poëzie informatief is.',
    typicalFamilies: ['STU-BIBLIOTHEEK'],
  },
  {
    id: 'stu.bibliotheek-nur-als-paginas',
    label: 'NUR als paginas',
    module: 'Studievaardig',
    description: "Leerling denkt dat de NUR-code het aantal pagina's is.",
    typicalFamilies: ['STU-BIBLIOTHEEK'],
  },
  {
    id: 'stu.bibliotheek-nur-als-jaar',
    label: 'NUR als jaar',
    module: 'Studievaardig',
    description: 'Leerling denkt dat de NUR-code het jaar van uitgave is.',
    typicalFamilies: ['STU-BIBLIOTHEEK'],
  },
  {
    id: 'stu.bibliotheek-nur-als-prijs',
    label: 'NUR als prijs',
    module: 'Studievaardig',
    description: 'Leerling denkt dat de NUR-code de prijs is.',
    typicalFamilies: ['STU-BIBLIOTHEEK'],
  },
  {
    id: 'stu.bibliotheek-zoeken-zonder-catalogus',
    label: 'Zoeken zonder catalogus',
    module: 'Studievaardig',
    description:
      'Leerling wil alle schappen langslopen in plaats van de catalogus te gebruiken.',
    typicalFamilies: ['STU-BIBLIOTHEEK'],
  },
  {
    id: 'stu.bibliotheek-vriend-als-catalogus',
    label: 'Vriend als catalogus',
    module: 'Studievaardig',
    description:
      'Leerling vraagt een vriend in plaats van de catalogus te gebruiken.',
    typicalFamilies: ['STU-BIBLIOTHEEK'],
  },
  {
    id: 'stu.bibliotheek-internet-als-catalogus',
    label: 'Internet als catalogus',
    module: 'Studievaardig',
    description:
      'Leerling zoekt op internet in plaats van in de bibliotheekcatalogus.',
    typicalFamilies: ['STU-BIBLIOTHEEK'],
  },

  // ── Nieuwe entries (seeds-driven gap closure, juni 2026) ──────────────────
  // SSOT-canoniek `stu.<kebab>` format. Seeds zijn parallel hernoemd in
  // STU-STAAF.json / STU-CIRKEL.json / STU-WOORDENBOEK.json.
  // ── STU-STAAF — staafdiagram aflezen & vergelijken ───────────────────────
  {
    id: 'stu.staaf-vergeet-laatste-staaf',
    label: 'Laatste staaf vergeten bij optellen',
    module: 'Studievaardigheden',
    description:
      'Leerling stopt te vroeg met optellen en vergeet de laatste staaf mee te tellen — typisch een werkgeheugen/leesvolgorde-fout bij staafdiagrammen met 4+ categorieën.',
    typicalFamilies: ['STU-STAAF'],
  },
  {
    id: 'stu.staaf-rekenfout-optellen',
    label: 'Rekenfout bij optellen van staafwaarden',
    module: 'Studievaardigheden',
    description:
      'Leerling leest de staven correct af maar maakt een rekenfout in het optellen (bijv. cijfer-fout, vergeten over te dragen).',
    typicalFamilies: ['STU-STAAF'],
  },
  {
    id: 'stu.staaf-totaal-fout-getal',
    label: 'Totaal gegeven dat niet past',
    module: 'Studievaardigheden',
    description:
      'Leerling geeft een totaal dat duidelijk niet bij de zichtbare staafhoogtes past — gecombineerde aflees- en rekenfout zonder check.',
    typicalFamilies: ['STU-STAAF'],
  },
  {
    id: 'stu.staaf-verwacht-boven-data',
    label: 'Verwachting overstemt data',
    module: 'Studievaardigheden',
    description:
      'Leerling antwoordt op basis van een vooroordeel of verwachting in plaats van te kijken wat de staven feitelijk tonen — confirmation bias op grafiekdata.',
    typicalFamilies: ['STU-STAAF'],
  },
  {
    id: 'stu.staaf-hoogste-laagste-omdraaien',
    label: 'Hoogste en laagste staaf omgedraaid',
    module: 'Studievaardigheden',
    description:
      'Leerling wisselt "hoogste" en "laagste" om bij een vraag over uitersten — taal- of richting-fout (min/max-verwisseling).',
    typicalFamilies: ['STU-STAAF'],
  },
  {
    id: 'stu.staaf-conclusie-zonder-aflezen',
    label: 'Conclusie zonder af te lezen',
    module: 'Studievaardigheden',
    description:
      'Leerling trekt een conclusie over een staafdiagram zonder de waarden daadwerkelijk af te lezen — typisch bij vragen waar de y-as is verborgen.',
    typicalFamilies: ['STU-STAAF'],
  },
  // ── STU-CIRKEL — cirkeldiagram interpreteren ─────────────────────────────
  {
    id: 'stu.cirkel-som-niet-100',
    label: 'Cirkelpercentages tellen niet op tot 100',
    module: 'Studievaardigheden',
    description:
      'Leerling herkent niet dat de percentages in een cirkeldiagram per definitie optellen tot 100% — gebruikt deze regel niet als checksum bij een ontbrekend segment.',
    typicalFamilies: ['STU-CIRKEL'],
  },
  {
    id: 'stu.cirkel-kleinste-grootste-omdraaien',
    label: 'Kleinste en grootste segment omgedraaid',
    module: 'Studievaardigheden',
    description:
      'Leerling wisselt het kleinste en grootste cirkelsegment om — min/max-verwisseling in cirkel-context, parallel aan stu.staaf-hoogste-laagste-omdraaien.',
    typicalFamilies: ['STU-CIRKEL'],
  },
  {
    id: 'stu.cirkel-percentage-breuk-koppeling',
    label: 'Percentage ↔ breuk koppeling fout',
    module: 'Studievaardigheden',
    description:
      'Leerling koppelt percentages en gangbare breuken in een cirkeldiagram verkeerd (bijv. 25% ≠ ¼, 50% ≠ ½). Cross-module link met get.proc-* familie.',
    typicalFamilies: ['STU-CIRKEL'],
  },
  // ── STU-WOORDENBOEK ───────────────────────────────────────────────────────
  {
    id: 'stu.woordenboek-afkorting-verwarren',
    label: 'Woordenboek-afkorting verward',
    module: 'Studievaardigheden',
    description:
      'Leerling verwart afkortingen in een woordenboeklemma (bijv. zn. / ww. / bn. / bw.) bij het bepalen van de woordsoort. Cross-module link met taa.soort-*.',
    typicalFamilies: ['STU-WOORDENBOEK'],
  },
  {
    id: 'stu.woordenboek-positie-schatten',
    label: 'Woordenboek-positie verkeerd geschat',
    module: 'Studievaardigheden',
    description:
      'Leerling schat de alfabetische positie van een woord in het woordenboek verkeerd in (bijv. zoekt "muis" in het achterste deel i.p.v. middenin).',
    typicalFamilies: ['STU-WOORDENBOEK'],
  },

  // ─── Seed-compatibility aliases (dot-notation IDs used by STU seeds) ─────
  // These mirror existing kebab-canonical entries but match the exact IDs
  // referenced in STU-MINDMAP and STU-SAMENVATTEN seeds. Registered to
  // resolve PEDAGOGY_MISCONCEPTION_UNKNOWN governance blockers (juni 2026).
  {
    id: 'stu.mindmap.center_details',
    label: 'Details in het midden (seed-alias)',
    module: 'Studievaardig',
    description:
      'Leerling plaatst details in het centrum van een mindmap i.p.v. het hoofdonderwerp. Seed-alias voor stu.mindmap-details-in-midden.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.mindmap.color_decoration',
    label: 'Kleur als decoratie gezien (seed-alias)',
    module: 'Studievaardig',
    description:
      'Leerling ziet kleuren in een mindmap als versiering i.p.v. als informatie-grouperende code. Seed-alias voor stu.mindmap-kleur-als-decoratie.',
    typicalFamilies: ['STU-MINDMAP'],
  },
  {
    id: 'stu.samenvatten.copy_verbatim',
    label: 'Samenvatten = overschrijven (seed-alias)',
    module: 'Studievaardig',
    description:
      'Leerling verwart samenvatten met letterlijk overschrijven van zinnen. Seed-alias voor stu.samenvatten-letterlijk-overschrijven.',
    typicalFamilies: ['STU-SAMENVATTEN'],
  },
]
